// Per-step wizard validation. Mirrors the inline error messages in UX doc §4.1.
// Each validator returns { valid: boolean, errors: { fieldName: message } }.

import { CHANNEL, OFFER, LIMITS, WIZARD_STEP } from '../constants';

const addError = (errors, field, message) => {
  if (!errors[field]) errors[field] = message;
};

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const minutesFromNow = (iso) => {
  if (!iso) return -Infinity;
  const delta = new Date(iso).getTime() - Date.now();
  return delta / 60_000;
};

export const validateBasics = (draft) => {
  const errors = {};
  const name = draft?.name ?? '';

  if (!isNonEmptyString(name)) {
    addError(errors, 'name', 'Give your campaign a name.');
  } else if (name.length > LIMITS.CAMPAIGN_NAME_MAX) {
    addError(errors, 'name', `Name must be ${LIMITS.CAMPAIGN_NAME_MAX} characters or fewer.`);
  }

  const channels = draft?.channels ?? [];
  if (channels.length === 0) {
    addError(errors, 'channels', 'Select at least one channel.');
  }

  const offer = draft?.offer;
  if (!Object.values(OFFER).includes(offer)) {
    addError(errors, 'offer', 'Pick an offer type.');
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateAudience = (draft) => {
  const errors = {};
  const filters = draft?.audience?.filters ?? [];
  const reach = draft?.audience?.estimatedReach;

  // No filters means the implicit "all students" audience — always valid.
  if (filters.length === 0) {
    return { valid: true, errors };
  }
  // With filters, only block when the preview has resolved AND came back empty.
  // While the preview is pending (reach == null) we let the user move forward.
  if (reach != null && reach < 1) {
    addError(errors, 'audience', 'This audience is empty. Broaden your filters.');
  }
  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateContent = (draft) => {
  const errors = {};
  const channels = draft?.channels ?? [];
  const offer = draft?.offer;

  if (channels.includes(CHANNEL.EMAIL)) {
    const variant = draft?.content?.variants?.[0];
    const subject = variant?.subject ?? '';
    const body = variant?.body ?? '';
    if (!isNonEmptyString(subject)) {
      addError(errors, 'subject', 'Add a subject line (up to 150 characters).');
    } else if (subject.length > LIMITS.SUBJECT_MAX) {
      addError(errors, 'subject', `Subject must be ${LIMITS.SUBJECT_MAX} characters or fewer.`);
    }
    const previewText = variant?.previewText ?? '';
    if (previewText.length > LIMITS.PREVIEW_TEXT_MAX) {
      addError(errors, 'previewText', 'Preview text is too long.');
    }
    if (!isNonEmptyString(body)) {
      addError(errors, 'body', 'Write the email body.');
    }
  }

  if (channels.includes(CHANNEL.NETWORK_UPSELL)) {
    const featured = draft?.content?.networkUpsell?.featuredCourseIds ?? [];
    // With a discount offer, an empty scope means store-wide — the card promotes
    // the offer itself, not specific courses, so featured courses are optional.
    // Invitation seeds featuredCourseIds from the invited course, so its own
    // validator covers the missing-course case.
    const requiresFeatured = offer === OFFER.NONE;
    if (requiresFeatured && featured.length < LIMITS.FEATURED_COURSES_MIN) {
      addError(errors, 'featuredCourses', 'Pick at least one course to feature.');
    } else if (featured.length > LIMITS.FEATURED_COURSES_MAX) {
      addError(errors, 'featuredCourses', `Feature up to ${LIMITS.FEATURED_COURSES_MAX} courses.`);
    }
  }

  if (offer === OFFER.INVITATION) {
    if (!draft?.invitation?.courseId) {
      addError(errors, 'invitationCourse', 'Pick the course to invite recipients to.');
    }
    const expiresAt = draft?.invitation?.expiresAt;
    if (!expiresAt || new Date(expiresAt).getTime() <= Date.now()) {
      addError(errors, 'invitationExpiry', 'Expiry must be in the future.');
    }
  }

  if (offer === OFFER.DISCOUNT) {
    const code = draft?.discount?.code ?? '';
    if (!isNonEmptyString(code)) {
      addError(errors, 'discountCode', 'Give the discount a code.');
    } else if (code.length < LIMITS.DISCOUNT_CODE_MIN || code.length > LIMITS.DISCOUNT_CODE_MAX) {
      addError(errors, 'discountCode', `Code must be ${LIMITS.DISCOUNT_CODE_MIN}–${LIMITS.DISCOUNT_CODE_MAX} characters.`);
    } else if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
      addError(errors, 'discountCode', 'Code can only contain letters, numbers, hyphens and underscores.');
    }
    const pct = draft?.discount?.percentage;
    if (pct != null && (pct < 1 || pct > 100)) {
      addError(errors, 'discountPercentage', 'Percentage must be between 1 and 100.');
    }
    const validUntil = draft?.discount?.validUntil;
    if (!validUntil || new Date(validUntil).getTime() <= Date.now()) {
      addError(errors, 'discountValidity', 'Valid-until must be in the future.');
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateSchedule = (draft) => {
  const errors = {};
  const channels = draft?.channels ?? [];

  if (channels.includes(CHANNEL.EMAIL)) {
    const email = draft?.schedule?.email ?? {};
    if (email.mode === 'scheduled') {
      if (!email.sendAt) {
        addError(errors, 'emailSendAt', 'Pick when to send the email.');
      } else if (minutesFromNow(email.sendAt) < LIMITS.SEND_AT_MIN_LEAD_MINUTES) {
        addError(errors, 'emailSendAt', `Pick a time at least ${LIMITS.SEND_AT_MIN_LEAD_MINUTES} minutes from now.`);
      }
    }
  }

  if (channels.includes(CHANNEL.NETWORK_UPSELL)) {
    const upsell = draft?.schedule?.networkUpsell ?? {};
    if (!upsell.activeFrom) {
      addError(errors, 'upsellActiveFrom', 'Pick when the placement should go live.');
    }
    if (!upsell.activeUntil) {
      addError(errors, 'upsellActiveUntil', 'Pick when the placement should end.');
    }
    if (upsell.activeFrom && upsell.activeUntil &&
        new Date(upsell.activeUntil).getTime() <= new Date(upsell.activeFrom).getTime()) {
      addError(errors, 'upsellActiveUntil', 'End must be after start.');
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateReview = (draft) => {
  // All earlier steps must have passed. Step 5 is read-only.
  const results = [validateBasics(draft), validateAudience(draft), validateContent(draft), validateSchedule(draft)];
  const allErrors = Object.assign({}, ...results.map((r) => r.errors));
  return { valid: results.every((r) => r.valid), errors: allErrors };
};

const VALIDATORS_BY_STEP = {
  [WIZARD_STEP.BASICS]: validateBasics,
  [WIZARD_STEP.AUDIENCE]: validateAudience,
  [WIZARD_STEP.CONTENT]: validateContent,
  [WIZARD_STEP.SCHEDULE]: validateSchedule,
  [WIZARD_STEP.REVIEW]: validateReview,
};

export const validateStep = (step, draft) => {
  const validator = VALIDATORS_BY_STEP[step];
  return validator ? validator(draft) : { valid: true, errors: {} };
};

export const isStepValid = (step, draft) => validateStep(step, draft).valid;

// Highest step reachable given current draft state — used to gate stepper jumps.
export const maxReachableStep = (draft) => {
  let max = WIZARD_STEP.BASICS;
  for (const step of [WIZARD_STEP.BASICS, WIZARD_STEP.AUDIENCE, WIZARD_STEP.CONTENT, WIZARD_STEP.SCHEDULE]) {
    if (isStepValid(step, draft)) max = step + 1;
    else break;
  }
  return Math.min(max, WIZARD_STEP.REVIEW);
};
